import {
  ChangeDetectionStrategy,
  Component,
  Input,
  booleanAttribute,
} from '@angular/core';

export type DsAvatarSize = 's' | 'm' | 'l';
export type DsAvatarVariant = 'default' | 'app';

@Component({
  selector: 'ds-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      [class]="avatarClasses"
      [attr.role]="decorative ? null : 'img'"
      [attr.aria-label]="decorative ? null : resolvedAriaLabel"
      [attr.aria-hidden]="decorative ? 'true' : null"
    >
      <span class="ds-avatar__initials">{{ displayInitials }}</span>
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
    }

    .ds-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      flex: 0 0 auto;
      overflow: hidden;
      border-radius: var(--ds-avatar-border-radius);
      background: var(--ds-avatar-background);
      color: var(--ds-avatar-foreground);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: var(--ds-avatar-font-weight);
      text-align: center;
      white-space: nowrap;
    }

    .ds-avatar__initials {
      display: block;
      text-box-edge: cap alphabetic;
      text-box-trim: trim-both;
    }

    .ds-avatar--app {
      background: var(--ds-avatar-app-background);
      color: var(--ds-avatar-app-foreground);
    }

    .ds-avatar--s {
      width: var(--ds-avatar-s-size);
      height: var(--ds-avatar-s-size);
      font-size: var(--ds-avatar-s-font-size);
      line-height: var(--ds-avatar-s-line-height);
    }

    .ds-avatar--m {
      width: var(--ds-avatar-m-size);
      height: var(--ds-avatar-m-size);
      font-size: var(--ds-avatar-m-font-size);
      line-height: var(--ds-avatar-m-line-height);
    }

    .ds-avatar--l {
      width: var(--ds-avatar-l-size);
      height: var(--ds-avatar-l-size);
      font-size: var(--ds-avatar-l-font-size);
      line-height: var(--ds-avatar-l-line-height);
    }
  `],
})
export class DsAvatarComponent {
  @Input() initials = 'FC';
  @Input() size: DsAvatarSize = 's';
  @Input() variant: DsAvatarVariant = 'default';
  @Input() ariaLabel = '';
  @Input({ transform: booleanAttribute }) decorative = false;

  get avatarClasses(): string {
    return `ds-avatar ds-avatar--${this.size} ds-avatar--${this.variant}`;
  }

  get displayInitials(): string {
    return this.initials.trim().slice(0, 2).toUpperCase();
  }

  get resolvedAriaLabel(): string {
    return this.ariaLabel || this.displayInitials;
  }
}
